function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import uniqueId from 'lodash.uniqueid';
import { FormControl, FormHelperText, InputLabel, makeStyles, MenuItem, Select } from '@material-ui/core';
import { setValue } from '../../lib/singleSelectHelpers';
import { OptionGroup, withGroupedOptionHelpers, withStandardOptionHelpers } from '../../lib/optionHelpers';
import IconWrapper from '../../lib/IconWrapper';
import useFormHelpers from '../../hooks/useFormHelpers';

const getLowResSize = ({
  size
}) => {
  switch (size) {
    case 'medium':
      return 24;

    case 'large':
      return 36;

    default:
      return undefined;
  }
};

const getHighResSize = ({
  size
}) => {
  switch (size) {
    case 'medium':
      return 36;

    case 'large':
      return 48;

    default:
      return undefined;
  }
};

const useStyles = makeStyles(theme => {
  return {
    formControl: {
      width: '100%'
    },
    icon: {
      fontSize: getLowResSize,
      top: ({
        size
      }) => `calc(50% - ${getLowResSize({
        size
      }) / 2}px)`,
      [theme.breakpoints.up('md')]: {
        fontSize: getHighResSize,
        top: ({
          size
        }) => `calc(50% - ${getHighResSize({
          size
        }) / 2}px)`
      }
    },
    inputLabel: {
      fontSize: getLowResSize,
      [theme.breakpoints.up('md')]: {
        fontSize: getHighResSize
      }
    },
    screenReaderOnly: {
      left: -10000,
      height: 0,
      position: 'absolute',
      width: 0
    },
    select: {
      fontSize: getLowResSize,
      marginTop: ({
        hideLabel
      }) => hideLabel ? '0px !important' : undefined,
      [theme.breakpoints.up('md')]: {
        fontSize: getHighResSize
      }
    }
  };
});

let SingleSelect = ({
  className,
  emptyOptionLabel,
  getOptionKey,
  getOptionLabel,
  getOptionType,
  getOptionValue,
  helperText,
  hideLabel,
  icon,
  label,
  onChange,
  options,
  renderSelectedOption,
  size,
  value: selectedOption = '',
  ...props
} = {}) => {
  const [inputId] = useState(() => uniqueId('singleSelect_'));
  const classes = useStyles({
    hideLabel,
    size
  });

  const defaultRenderValue = value => {
    const foundOption = options.find(option => getOptionValue(option) === value);
    const label = foundOption ? getOptionLabel(foundOption) : null;
    return React.createElement("div", null, label);
  };

  return React.createElement(IconWrapper, {
    icon: icon
  }, React.createElement(FormControl, {
    className: `${classes.formControl} ${className || ''}`
  }, React.createElement(InputLabel, {
    className: hideLabel ? classes.screenReaderOnly : undefined,
    htmlFor: inputId,
    error: props.error
  }, label), React.createElement(Select, _extends({
    value: selectedOption,
    onChange: setValue(options, getOptionValue, onChange),
    inputProps: {
      id: inputId
    },
    renderValue: renderSelectedOption || defaultRenderValue,
    className: classes.select,
    classes: {
      icon: classes.icon
    }
  }, props), emptyOptionLabel && React.createElement(MenuItem, {
    value: undefined
  }, emptyOptionLabel), options.map(option => getOptionType(option) === 'group' ? React.createElement(OptionGroup, {
    key: getOptionKey(option)
  }, getOptionLabel(option)) : React.createElement(MenuItem, {
    key: getOptionKey(option),
    value: getOptionValue(option)
  }, getOptionLabel(option)))), helperText && React.createElement(FormHelperText, {
    error: props.error
  }, helperText)));
};

SingleSelect.propTypes = {
  className: PropTypes.string,
  emptyOptionLabel: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  hideLabel: PropTypes.bool,
  icon: PropTypes.element,
  label: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  renderSelectedOption: PropTypes.func,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  value: PropTypes.any
};
SingleSelect.defaultProps = {
  hideLabel: false,
  size: 'small'
};
SingleSelect = withGroupedOptionHelpers(withStandardOptionHelpers(SingleSelect));

const LinkedSingleSelect = props => {
  const helpers = useFormHelpers(props);
  return React.createElement(SingleSelect, helpers);
};

LinkedSingleSelect.propTypes = {
  name: PropTypes.string.isRequired,
  showErrorBeforeTouched: PropTypes.bool
};
LinkedSingleSelect.defaultProps = {
  showErrorBeforeTouched: true
};
export { LinkedSingleSelect, SingleSelect };